import { ArrowRight, BookOpen, CalendarCheck, CheckCircle2, Clock3, FileText, FolderGit2, Loader2, RotateCcw, Target } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  completeStudentDailyTask,
  generateAiGrowthTasks,
  getAiGrowthTasks,
  getLatestAiGrowthPlan,
  getStudentGrowthDashboard,
  getStudentGrowthToday,
  skipStudentDailyTask,
  updateAiGrowthTask,
} from '../../api.js'

const typeLabels = { learn: '学习', learning: '学习', practice: '练习', project: '项目', review: '复盘' }
const typeIcons = { learn: BookOpen, learning: BookOpen, practice: Target, project: FolderGit2, review: FileText }

function taskLink(task) {
  if (task.sourceType === 'ai_growth_plan' && task.sourceId) return `/learning/ai/${encodeURIComponent(task.sourceId)}`
  if (task.sourceType === 'learning_node' && task.sourceId) return `/learning/${task.sourceId}`
  if (task.sourceType === 'practice_task' && task.sourceId) return `/learning/practice/${task.sourceId}`
  if (task.sourceType === 'project_plan' && task.sourceId) return '/projects/recommendations'
  if (task.sourceType === 'growth_plan' && task.taskType === 'project') return '/projects/recommendations'
  if (task.sourceType === 'growth_plan') return '/learning'
  if (task.taskType === 'learning') return '/learning'
  if (task.taskType === 'practice') return '/learning'
  if (task.taskType === 'project') return '/projects/recommendations'
  return ''
}

function aiTaskToTodayTask(task) {
  const detail = task.detail || {}
  return {
    id: task.id,
    planId: task.planId,
    title: task.title,
    description: detail.professionalGoal || detail.whyItMatters || '按照专属路线完成当前专业任务。',
    taskType: task.type === 'learn' ? 'learning' : task.type,
    estimatedMinutes: Number(task.durationMinutes || 0),
    xpReward: task.type === 'project' ? 30 : task.type === 'practice' ? 20 : 15,
    status: task.status,
    sourceType: 'ai_growth_plan',
    sourceId: task.nodeId,
    detail,
  }
}

function StudentTodayPage() {
  const [today, setToday] = useState({ tasks: [], summary: null })
  const [dashboard, setDashboard] = useState(null)
  const [loading, setLoading] = useState(true)
  const [workingId, setWorkingId] = useState('')
  const [error, setError] = useState('')

  function load() {
    setLoading(true)
    setError('')
    const controller = new AbortController()
    getLatestAiGrowthPlan(controller.signal)
      .catch(() => ({ plan: null }))
      .then(async ({ plan }) => {
        const isDetailedAiPlan = plan?.source === 'ai_generated' && Number(plan?.schemaVersion) >= 2
        if (isDetailedAiPlan) {
          let taskPayload = await getAiGrowthTasks(controller.signal)
          if (!taskPayload?.tasks?.length) taskPayload = await generateAiGrowthTasks(controller.signal)
          const tasks = (taskPayload?.tasks || []).map(aiTaskToTodayTask)
          return [{
            date: tasks[0]?.taskDate || new Date().toISOString().slice(0, 10),
            tasks,
            summary: {
              totalTasks: tasks.length,
              completedTasks: tasks.filter((task) => task.status === 'completed').length,
              totalXp: tasks.reduce((sum, task) => sum + task.xpReward, 0),
              estimatedMinutes: tasks.reduce((sum, task) => sum + task.estimatedMinutes, 0),
            },
          }, {
            source: 'ai_growth_plan',
            target: plan.targetCareer || plan.target,
          }]
        }
        return Promise.all([
          getStudentGrowthToday(controller.signal),
          getStudentGrowthDashboard(controller.signal).catch(() => null),
        ])
      })
      .then(([todayPayload, dashboardPayload]) => {
        const tasks = Array.isArray(todayPayload?.tasks) ? todayPayload.tasks : []
        setToday({
          tasks,
          summary: todayPayload?.summary || null,
          date: todayPayload?.date || new Date().toISOString().slice(0, 10),
        })
        setDashboard(dashboardPayload || null)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        setToday({ tasks: [], summary: null, date: new Date().toISOString().slice(0, 10) })
        setError(err.message || '今日成长计划加载失败，请稍后重试。')
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }

  useEffect(() => load(), [])

  const summary = today.summary || {
    totalTasks: today.tasks.length,
    completedTasks: today.tasks.filter((task) => task.status === 'completed').length,
    totalXp: today.tasks.reduce((sum, task) => sum + Number(task.xpReward || 0), 0),
    estimatedMinutes: today.tasks.reduce((sum, task) => sum + Number(task.estimatedMinutes || 0), 0),
  }
  const progress = summary.totalTasks ? Math.round((summary.completedTasks / summary.totalTasks) * 100) : 0
  const pendingTasks = useMemo(() => today.tasks.filter((task) => task.status === 'pending'), [today.tasks])

  async function updateTask(task, action) {
    if (workingId || task.status === 'completed') return
    setWorkingId(task.id)
    setError('')
    try {
      if (task.sourceType === 'ai_growth_plan') {
        await updateAiGrowthTask(task.id, { action: action === 'skip' ? 'skip' : 'complete' })
      } else if (action === 'skip') await skipStudentDailyTask(task.id)
      else await completeStudentDailyTask(task.id)
      load()
    } catch (err) {
      setError(err.message || '任务更新失败，请稍后重试。')
    } finally {
      setWorkingId('')
    }
  }

  return (
    <main className="page-container student-roadmap-page student-today-page">
      <section className="student-roadmap-stage-hero learning-os-stage-hero">
        <div>
          <span><CalendarCheck size={18} /> 今日成长计划 · {today.date || new Date().toISOString().slice(0, 10)}</span>
          <h1>{loading ? '正在加载今日计划...' : `今天还有 ${pendingTasks.length} 项待完成`}</h1>
          <p>{dashboard?.source === 'ai_growth_plan'
            ? `按「${dashboard.target}」专属路线完成当前节点，完成后继续下一项。`
            : pendingTasks[0]?.description || '建议先完成当前路线的下一个学习节点，再做练习，最后推进一个项目成果。'}</p>
        </div>
        <div className="student-growth-summary">
          <span>{summary.totalTasks || 0} 项任务</span>
          <span>{summary.estimatedMinutes || 0} 分钟</span>
          <span>{summary.totalXp || 0} XP</span>
        </div>
      </section>

      {error ? (
        <div className="student-growth-error">
          <strong>今日任务加载失败</strong>
          <p>{error}</p>
          <Link className="secondary-button" to="/growth">返回成长中心</Link>
        </div>
      ) : null}

      <section className="student-today-overview">
        <article>
          <span><CheckCircle2 size={16} /> 完成进度</span>
          <strong>{progress}%</strong>
          <div className="student-today-progress"><i style={{ width: `${progress}%` }} /></div>
          <p>{summary.completedTasks || 0}/{summary.totalTasks || 0} 项已完成</p>
        </article>
        <article><span><Clock3 size={16} /> 预计学习时间</span><strong>{summary.estimatedMinutes || 0} 分钟</strong><p>按学习、练习、项目和复盘组合生成。</p></article>
        <article><span><Target size={16} /> 可获得 XP</span><strong>{summary.totalXp || 0}</strong><p>同一任务只奖励一次。</p></article>
      </section>

      <section className="student-today-task-list">
        {loading ? <p className="student-growth-error is-success">正在加载今日任务...</p> : null}
        {!loading && !today.tasks.length ? (
          <article className="student-today-task-card">
            <div className="student-today-task-head"><span><CalendarCheck size={16} /> 空状态</span><small>暂无任务</small></div>
            <h2>今天暂时没有任务</h2>
            <p>可以先进入学习路线，选择一个节点开始学习；系统会根据你的路线继续生成今日计划。</p>
            <div className="student-today-actions">
              <Link className="primary-button" to="/learning">进入学习路线 <ArrowRight size={16} /></Link>
              <Link className="secondary-button" to="/growth">返回成长中心</Link>
            </div>
          </article>
        ) : null}
        {today.tasks.map((task) => {
          const Icon = typeIcons[task.taskType] || Target
          const link = taskLink(task)
          const done = task.status === 'completed'
          const skipped = task.status === 'skipped'
          return (
            <article className={`student-today-task-card ${done ? 'is-done' : skipped ? 'is-skipped' : ''}`} key={task.id}>
              <div className="student-today-task-head">
                <span><Icon size={16} /> {typeLabels[task.taskType] || '任务'}</span>
                <small>{done ? '已完成' : skipped ? '已跳过' : '待完成'}</small>
              </div>
              <h2>{task.title || '今日任务'}</h2>
              <p>{task.description || '完成这个任务后回到今日计划标记完成。'}</p>
              <div className="project-library-tags">
                <small>{task.estimatedMinutes || 20} 分钟</small>
                <small>{task.xpReward || 0} XP</small>
                <small>{task.sourceType || task.taskType || 'growth'}</small>
              </div>
              <div className="student-today-actions">
                {link ? <Link className="secondary-button" to={link}>去完成 <ArrowRight size={16} /></Link> : null}
                <button className="primary-button" type="button" disabled={done || workingId === task.id} onClick={() => updateTask(task, 'complete')}>
                  {workingId === task.id ? <Loader2 size={16} /> : <CheckCircle2 size={16} />}
                  {done ? '已完成' : '完成'}
                </button>
                {!done ? <button className="secondary-button" type="button" disabled={workingId === task.id} onClick={() => updateTask(task, 'skip')}><RotateCcw size={16} /> 跳过</button> : null}
              </div>
            </article>
          )
        })}
      </section>
    </main>
  )
}

export default StudentTodayPage
